import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialize Functions with europe-west3 region
const functions = getFunctions(undefined, 'europe-west3');

/**
 * Get all activities with resource summaries
 */
export async function getAllActivitiesWithResources() {
  try {
    const getAllActivities = httpsCallable(functions, 'getAllActivitiesWithResourcesFunc');
    const result = await getAllActivities();
    return result.data;
  } catch (error) {
    console.error('Error getting activities with resources:', error);
    throw error;
  }
}

/**
 * Get activities missing videos or resources
 */
export async function getActivitiesMissingResources() {
  try {
    const getMissing = httpsCallable(functions, 'getActivitiesMissingResourcesFunc');
    const result = await getMissing();
    return result.data;
  } catch (error) {
    console.error('Error getting activities missing resources:', error);
    throw error;
  }
}

/**
 * Trigger bulk check of all resources
 */
export async function bulkCheckResources() {
  try {
    const bulkCheck = httpsCallable(functions, 'bulkCheckResourcesFunc');
    const result = await bulkCheck();
    return result.data;
  } catch (error) {
    console.error('Error triggering bulk check:', error);
    throw error;
  }
}

/**
 * Update resource review status
 */
export async function updateResourceReview(data) {
  try {
    const updateReview = httpsCallable(functions, 'updateResourceReviewFunc');
    const result = await updateReview(data);
    return result.data;
  } catch (error) {
    console.error('Error updating resource review:', error);
    throw error;
  }
}

/**
 * Delete a resource from an activity
 */
export async function deleteResource(activityId, resourceIndex) {
  try {
    const deleteRes = httpsCallable(functions, 'deleteResourceFunc');
    const result = await deleteRes({ activityId, resourceIndex });
    return result.data;
  } catch (error) {
    console.error('Error deleting resource:', error);
    throw error;
  }
}

/**
 * Calculate appropriateness score for a resource
 */
export async function calculateAppropriatenessScore(data) {
  try {
    const calculateScore = httpsCallable(functions, 'calculateAppropriatenessScoreFuncExport');
    const result = await calculateScore(data);
    return result.data;
  } catch (error) {
    console.error('Error calculating appropriateness score:', error);
    throw error;
  }
}

/**
 * Search YouTube for videos related to an activity
 */
export async function searchVideosForActivity(data) {
  try {
    const searchVideos = httpsCallable(functions, 'searchVideosForActivityFunc');
    const result = await searchVideos(data);
    return result.data;
  } catch (error) {
    console.error('Error searching videos for activity:', error);
    throw error;
  }
}

/**
 * Bulk search videos for all activities missing videos
 * @param {Object} options - Optional parameters
 * @param {number} options.limit - Maximum number of activities to process (default: 20)
 * @param {number} options.offset - Number of activities to skip (default: 0)
 * @param {boolean} options.skipWithPendingAssignments - Skip activities with pending assignments (default: true)
 * @param {boolean} options.includeBrokenOnly - Only include activities with broken videos (default: false)
 */
export async function bulkSearchAllActivities(options = {}) {
  try {
    const bulkSearch = httpsCallable(functions, 'bulkSearchAllActivitiesFunc', {
      timeout: 540000, // 540 seconds to match server timeout
    });
    const result = await bulkSearch(options);
    return result.data;
  } catch (error) {
    console.error('Error bulk searching activities:', error);
    throw error;
  }
}

/**
 * Get pending video assignments
 */
export async function getPendingVideoAssignments() {
  try {
    const getAssignments = httpsCallable(functions, 'getPendingVideoAssignmentsFunc');
    const result = await getAssignments();
    return result.data;
  } catch (error) {
    console.error('Error getting pending video assignments:', error);
    throw error;
  }
}

/**
 * Accept a video candidate and add it to the activity
 */
export async function acceptVideoCandidate(data) {
  try {
    const accept = httpsCallable(functions, 'acceptVideoCandidateFunc');
    const result = await accept(data);
    return result.data;
  } catch (error) {
    console.error('Error accepting video candidate:', error);
    throw error;
  }
}

/**
 * Calculate video score with client-provided transcript
 */
export async function calculateVideoScore(data) {
  try {
    const calculateScore = httpsCallable(functions, 'calculateVideoScoreFunc');
    const result = await calculateScore(data);
    return result.data;
  } catch (error) {
    console.error('Error calculating video score:', error);
    throw error;
  }
}

// ============================================================================
// Product Search Functions
// ============================================================================

/**
 * Search Amazon for products related to an activity
 */
export async function searchProductsForActivity(data) {
  try {
    const searchProducts = httpsCallable(functions, 'searchProductsForActivityFunc');
    const result = await searchProducts(data);
    return result.data;
  } catch (error) {
    console.error('Error searching products for activity:', error);
    throw error;
  }
}

/**
 * Accept a product candidate and add it as a resource
 */
export async function acceptProductCandidate(data) {
  try {
    const accept = httpsCallable(functions, 'acceptProductCandidateFunc');
    const result = await accept(data);
    return result.data;
  } catch (error) {
    console.error('Error accepting product candidate:', error);
    throw error;
  }
}

/**
 * Bulk search products for all activities missing buy resources
 * @param {Object} options - Optional parameters
 * @param {number} options.limit - Maximum number of activities to process (default: 20)
 * @param {number} options.offset - Number of activities to skip (default: 0)
 */
export async function bulkSearchProductsForAllActivities(options = {}) {
  try {
    const bulkSearch = httpsCallable(functions, 'bulkSearchProductsForAllActivitiesFunc', {
      timeout: 540000, // 540 seconds to match server timeout
    });
    const result = await bulkSearch(options);
    return result.data;
  } catch (error) {
    console.error('Error bulk searching products:', error);
    throw error;
  }
}

/**
 * Get pending resource (product) assignments
 */
export async function getPendingResourceAssignments() {
  try {
    const getAssignments = httpsCallable(functions, 'getPendingResourceAssignmentsFunc');
    const result = await getAssignments();
    return result.data;
  } catch (error) {
    console.error('Error getting pending resource assignments:', error);
    throw error;
  }
}